import * as React from 'react'

import { Check } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { cn } from '@/lib/utils'
import { IProject } from '~/client/graph'
import ClockComponent from '~/client/src/shared/components/Clock'
import { FieldTrigger } from '~/client/src/shared/components/FieldTrigger'
import { OptionsHeader } from '~/client/src/shared/components/OptionsList'
import { ProjectLogo } from '~/client/src/shared/components/ProjectLogo/ProjectLogo'
import ResponsiveSurface from '~/client/src/shared/components/ResponsiveSurface/ResponsiveSurface'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/client/src/shared/components/shadcn-ui/command'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Project from '~/client/src/shared/models/Project'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { NOOP } from '~/client/src/shared/utils/noop'

interface IProjectSelectorContext {
  isDisabled: boolean
  projectName: string
}

const ProjectSelectorContext =
  React.createContext<IProjectSelectorContext | null>(null)

function useProjectSelectorContext(): IProjectSelectorContext {
  const ctx = React.useContext(ProjectSelectorContext)
  if (!ctx) {
    throw new Error(
      'ProjectSelector trigger must be rendered inside <ProjectSelector>',
    )
  }
  return ctx
}

interface IProjectSelectorProps {
  isDisabled?: boolean
  isLoading?: boolean
  shouldShowNewProjectItem?: boolean
  currentProject: Project
  projects: IProject[]
  projectDateStore?: ProjectDateStore
  projectsStore?: ProjectsStore
  onItemSelect: (item: Project) => void
  onNewProjectSelected?: () => void
  /** Renders the list in a bottom sheet instead of a popover. */
  isMobileMode?: boolean
  /** The trigger. Rendered inside the surface, so it sees the context. */
  children: React.ReactElement
}

const NEW_PROJECT_KEY = '__new-project__'
const DESKTOP_LIST_CLASS = 'tw:max-h-[min(620px,55vh)]'
const MOBILE_LIST_CLASS = 'tw:max-h-none tw:min-h-0 tw:flex-1'

const ProjectSelector: React.FC<IProjectSelectorProps> = ({
  isDisabled = false,
  isLoading = false,
  shouldShowNewProjectItem = false,
  currentProject,
  projects = [],
  projectDateStore,
  projectsStore,
  onItemSelect = NOOP,
  onNewProjectSelected = NOOP,
  isMobileMode = false,
  children,
}) => {
  const [open, setOpen] = React.useState(false)

  const isSelectingDisabled = projects.length === 0 || isLoading || isDisabled

  const projectName = currentProject?.name || Localization.translator.newProject

  const handleSelect = (project: Project | string) => {
    setOpen(false)

    if (shouldShowNewProjectItem && typeof project === 'string') {
      onNewProjectSelected()
      return
    }

    onItemSelect(project as Project)
  }

  const ctx = React.useMemo<IProjectSelectorContext>(
    () => ({
      isDisabled: isSelectingDisabled,
      projectName,
    }),
    [isSelectingDisabled, projectName],
  )

  return (
    <ProjectSelectorContext.Provider value={ctx}>
      <ResponsiveSurface
        open={open}
        onOpenChange={setOpen}
        title={Localization.translator.chooseProject}
        isMobileMode={isMobileMode}
        sizing={isMobileMode ? 'fill' : 'content'}
        width="wide"
        // 4, not the shell default 8 — keeps the desktop popover in place
        sideOffset={4}
        trigger={children}
      >
        <Command
          autoHighlightFirst={!isMobileMode}
          highlightOnPress={isMobileMode}
          defaultValue={isMobileMode ? undefined : currentProject?.id}
        >
          {isMobileMode && (
            <div className="tw:p-2.5">
              <OptionsHeader
                title={Localization.translator.chooseProject}
                onClose={() => setOpen(false)}
              />
            </div>
          )}
          <CommandInput
            data-testid="project-selector-search"
            placeholder={Localization.translator.search}
          />
          {/* the list owns pan-y, so vaul must not read a flick as swipe-to-dismiss */}
          <CommandList
            data-vaul-no-drag={isMobileMode ? '' : undefined}
            className={isMobileMode ? MOBILE_LIST_CLASS : DESKTOP_LIST_CLASS}
          >
            <CommandEmpty>
              {Localization.translator.noResultsFound}
            </CommandEmpty>
            <CommandGroup>
              {shouldShowNewProjectItem && (
                <CommandItem
                  key={NEW_PROJECT_KEY}
                  value={Localization.translator.newProject}
                  onSelect={() =>
                    handleSelect(Localization.translator.newProject)
                  }
                >
                  <div className="tw:text-app-dense-medium">
                    {Localization.translator.newProject}
                  </div>
                </CommandItem>
              )}
              {projects.map(project => {
                const {
                  id,
                  name,
                  dateTimeFormat,
                  timezoneId,
                  logoUrl,
                  isLogoWhite,
                } = project as Project
                const address = projectsStore.getAddressString(id)
                const isActive = currentProject?.id === id

                return (
                  <CommandItem
                    key={id}
                    data-testid={`project-option-${id}`}
                    value={id}
                    keywords={[name, address]}
                    onSelect={() => handleSelect(project as Project)}
                    className={cn('tw:py-2', isActive && 'tw:bg-primary-soft')}
                  >
                    <ProjectLogo
                      logoUrl={logoUrl}
                      className="tw:mr-2 tw:flex tw:h-8 tw:w-12 tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded tw:[&_img]:h-full tw:[&_img]:max-h-full tw:[&_img]:max-w-full tw:[&_img]:object-contain tw:[&_img]:w-full"
                      isLogoWhite={isLogoWhite}
                    />
                    <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                      <div className="tw:text-app-dense-medium tw:truncate">
                        {name}
                      </div>
                      <div className="tw:text-app-micro tw:text-muted-foreground tw:truncate">
                        <ClockComponent
                          format={
                            projectDateStore.getDateFormat(dateTimeFormat)?.time
                          }
                          timezoneId={
                            timezoneId || projectDateStore.userTimezone
                          }
                        />
                        {address ? ` | ${address}` : ''}
                      </div>
                    </div>
                    {isActive && (
                      <Check className="tw:ml-2 tw:size-4 tw:shrink-0 tw:text-primary" />
                    )}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </ResponsiveSurface>
    </ProjectSelectorContext.Provider>
  )
}

// `!` is load-bearing: legacy styles.scss loads after Tailwind and would win.
// Slot/`render` clone the trigger and inject props, so it must forward them —
// FieldTrigger already does, so these only supply the value and disabled state.
type ITriggerProps = React.ComponentPropsWithoutRef<typeof FieldTrigger>

const ProjectSelectorTriggerSurface = React.forwardRef<
  HTMLButtonElement,
  ITriggerProps
>((props, ref) => {
  const { isDisabled, projectName } = useProjectSelectorContext()
  return (
    <FieldTrigger {...props} ref={ref} disabled={isDisabled}>
      {projectName}
    </FieldTrigger>
  )
})
ProjectSelectorTriggerSurface.displayName = 'ProjectSelectorTriggerSurface'

const ProjectSelectorTriggerNavBar = React.forwardRef<
  HTMLButtonElement,
  ITriggerProps
>(({ className, ...props }, ref) => {
  const { isDisabled, projectName } = useProjectSelectorContext()
  return (
    <FieldTrigger
      {...props}
      ref={ref}
      data-testid="navbar-project-selector"
      disabled={isDisabled}
      className={cn(
        'tw:w-[200px] tw:max-w-[200px] tw:border-0 tw:bg-white/5 tw:px-3 tw:text-zinc-200 tw:hover:bg-white/10 tw:[&_svg]:text-zinc-200',
        className,
      )}
    >
      {projectName}
    </FieldTrigger>
  )
})
ProjectSelectorTriggerNavBar.displayName = 'ProjectSelectorTriggerNavBar'

const ProjectSelectorRoot = inject(
  'projectsStore',
  'projectDateStore',
)(observer(ProjectSelector))

export default ProjectSelectorRoot

export { ProjectSelectorTriggerNavBar, ProjectSelectorTriggerSurface }
